/**
 * The name of every skill, in one place — read by the skill's own declaration and by anything
 * that mentions it: other skills' instructions, tool descriptions, lint findings, prompts.
 *
 * This module imports nothing, so a name is readable at module-eval time from anywhere,
 * including a module that sits in an import cycle.
 *
 * The codeo and html-design registries define the same animation skills under the same names,
 * so both read the same entry.
 *
 */
export const SKILL_NAMES = {
    aiVoiceoverVideoBasics: 'ai-voiceover-video-basics',
    ambientFloatLoop: 'ambient-float-loop',
    ambientParticleField: 'ambient-particle-field',
    atmosphereGradientField: 'atmosphere-gradient-field',
    beatSyncAccent: 'beat-sync-accent',
    calloutWithLeaderLine: 'callout-with-leader-line',
    cardGridAssemble: 'card-grid-assemble',
    changeTemplate: 'change-template',
    chartStoryBuild: 'chart-story-build',
    cinematicCameraMove: 'cinematic-camera-move',
    cornerBadgeStack: 'corner-badge-stack',
    counterTickUp: 'counter-tick-up',
    createAnimatedVideo: 'create-animated-video',
    applyBrandKit: 'apply-brand-kit',
    createBrandKit: 'create-brand-kit',
    createBoldCaptionsVideo: 'create-bold-captions-video',
    createBrandStoryVideo: 'create-brand-story-video',
    createListAndStepsVideo: 'create-list-and-steps-video',
    createMediaMontageVideo: 'create-media-montage-video',
    createMotionGraphicsVideo: 'create-motion-graphics-video',
    createPodcastWaveformVideo: 'create-podcast-waveform-video',
    createPromoAd: 'create-promo-ad',
    createQuotesShowcaseVideo: 'create-quotes-showcase-video',
    createReportSummaryVideo: 'create-report-summary-video',
    createSimilarToVideo: 'create-similar-to-video',
    createSlideNarrationVideo: 'create-slide-narration-video',
    createStoryboardVideo: 'create-storyboard-video',
    createTalkingHeadExplainerVideo: 'create-talking-head-explainer-video',
    createVideo: 'create-video',
    cropMedia: 'crop-media',
    dataAsMetaphor: 'data-as-metaphor',
    deviceScreenDive: 'device-screen-dive',
    documentBasics: 'document-basics',
    driftAndHold: 'drift-and-hold',
    echoTrailGhosts: 'echo-trail-ghosts',
    editVideoDuration: 'edit-video-duration',
    elementMorphHandoff: 'element-morph-handoff',
    emphasisWordPulse: 'emphasis-word-pulse',
    facetMorphShape: 'facet-morph-shape',
    feedScrollDemo: 'feed-scroll-demo',
    findDamMedia: 'find-dam-media',
    findStockMedia: 'find-stock-media',
    findUserMedia: 'find-user-media',
    fixTextContrast: 'fix-text-contrast',
    fixTextHierarchy: 'fix-text-hierarchy',
    fixTextWrapping: 'fix-text-wrapping',
    gatherRequirements: 'gather-requirements',
    glitchSliceText: 'glitch-slice-text',
    grainAndBloom: 'grain-and-bloom',
    halftoneDotDissolve: 'halftone-dot-dissolve',
    handDrawnAnnotation: 'hand-drawn-annotation',
    headlineSlam: 'headline-slam',
    htmlComposition: 'html-composition',
    hubAndSpokes: 'hub-and-spokes',
    iconSetConsistency: 'icon-set-consistency',
    iconSwapCycle: 'icon-swap-cycle',
    inlineHighlightChip: 'inline-highlight-chip',
    interfaceStateChange: 'interface-state-change',
    irisReveal: 'iris-reveal',
    kineticWordReveal: 'kinetic-word-reveal',
    knockoutSilhouetteIcon: 'knockout-silhouette-icon',
    letterSpacingSettle: 'letter-spacing-settle',
    lightSweepPass: 'light-sweep-pass',
    logoWallAssemble: 'logo-wall-assemble',
    lowerThirdCallout: 'lower-third-callout',
    markerEmphasis: 'marker-emphasis',
    meshGradientBackground: 'mesh-gradient-background',
    momentSweepTransition: 'moment-sweep-transition',
    morphChainSpine: 'morph-chain-spine',
    motionBlurStreak: 'motion-blur-streak',
    motionGraphicsScenesBasics: 'motion-graphics-scenes-basics',
    motionPolish: 'motion-polish',
    multiDeviceSplay: 'multi-device-splay',
    numberWheelRoll: 'number-wheel-roll',
    objectConstructionMethod: 'object-construction-method',
    orderedDitherPass: 'ordered-dither-pass',
    outlineToFillType: 'outline-to-fill-type',
    panBetweenStations: 'pan-between-stations',
    parallaxDepthDrift: 'parallax-depth-drift',
    perspectiveMarquee: 'perspective-marquee',
    pullBackReveal: 'pull-back-reveal',
    quoteProofPanel: 'quote-proof-panel',
    radialSurround: 'radial-surround',
    radialTickSystem: 'radial-tick-system',
    ratingAndProgressFill: 'rating-and-progress-fill',
    recordedVoiceoverVideoBasics: 'recorded-voiceover-video-basics',
    reviewVideo: 'review-video',
    scrambleDecodeReveal: 'scramble-decode-reveal',
    shapeMatchCut: 'shape-match-cut',
    sharedAxisSlide: 'shared-axis-slide',
    simulatedCursorClick: 'simulated-cursor-click',
    slatWipeReveal: 'slat-wipe-reveal',
    softFocusResolve: 'soft-focus-resolve',
    splitComparisonCards: 'split-comparison-cards',
    splitFlapBoard: 'split-flap-board',
    spotlightFocusCard: 'spotlight-focus-card',
    springSettle: 'spring-settle',
    staggeredListReveal: 'staggered-list-reveal',
    statBarsAndFills: 'stat-bars-and-fills',
    stopMotionCadence: 'stop-motion-cadence',
    streamingTextResponse: 'streaming-text-response',
    strikethroughReplace: 'strikethrough-replace',
    sustainedSceneReveal: 'sustained-scene-reveal',
    svgDrawOn: 'svg-draw-on',
    svgIconEnrichment: 'svg-icon-enrichment',
    talkingHeadVideoBasics: 'talking-head-video-basics',
    textureMaskedType: 'texture-masked-type',
    textVideoBasics: 'text-video-basics',
    thirdPartyVideoLink: 'third-party-video-link',
    tickerCrawlStrip: 'ticker-crawl-strip',
    timelineRailAdvance: 'timeline-rail-advance',
    typeAsObject: 'type-as-object',
    typewriterWithCaret: 'typewriter-with-caret',
    velocityMatchedCut: 'velocity-matched-cut',
    vignetteFocusPull: 'vignette-focus-pull',
    voiceoverVideoBasics: 'voiceover-video-basics',
    weightedExit: 'weighted-exit',
    whipPanCut: 'whip-pan-cut',
    wordSwapInPlace: 'word-swap-in-place',
    writeTextVideoScript: 'write-text-video-script',
    writeVoicedrivenVideoScript: 'write-voicedriven-video-script',
    zoomThroughTransition: 'zoom-through-transition',
} as const;
